import React, { memo } from 'react';
import { View, TouchableOpacity, StyleSheet, Modal, ActivityIndicator } from 'react-native';
import { Text } from '../../../components/ui/AppText';
import { LinearGradient } from 'expo-linear-gradient';
import Animated, { SlideInUp, FadeIn } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';

const ChallengeConfirmModal = memo(({
    visible,
    opponent,
    opponentUsername,
    remainingChallenges,
    onConfirm,
    onCancel,
    onClose,
    isLoading = false,
}) => {
    const displayUsername = opponentUsername || opponent?.username || 'usuário';

    const handleConfirm = () => {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
        onConfirm();
    };

    const handleCancel = () => {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
        (onCancel || onClose)?.();
    };

    const getLimitColor = () => {
        if (remainingChallenges <= 1) return '#FF9500';
        return '#34C759';
    };

    if (!visible) return null;

    return (
        <Modal visible={visible} transparent animationType="none" statusBarTranslucent>
            <Animated.View entering={FadeIn.duration(200)} style={styles.overlay}>
                <Animated.View entering={SlideInUp.springify()} style={styles.modal}>
                    <LinearGradient
                        colors={['rgba(45, 35, 75, 0.98)', 'rgba(26, 11, 46, 0.99)']}
                        style={StyleSheet.absoluteFill}
                    />

                    {/* Icon */}
                    <Text style={styles.icon}>⚔️</Text>

                    {/* Title */}
                    <Text style={styles.title}>Desafiar @{displayUsername}?</Text>

                    {/* Info Box */}
                    <View style={styles.infoBox}>
                        <View style={styles.infoRow}>
                            <Text style={styles.infoIcon}>📋</Text>
                            <Text style={styles.infoText}>10 questões aleatórias</Text>
                        </View>
                        <View style={styles.infoRow}>
                            <Text style={styles.infoIcon}>⏱️</Text>
                            <Text style={styles.infoText}>5 minutos para responder</Text>
                        </View>
                        <View style={styles.infoRow}>
                            <Text style={styles.infoIcon}>🏆</Text>
                            <Text style={styles.infoText}>Quem acertar mais vence</Text>
                        </View>

                        <View style={styles.divider} />

                        <Text style={styles.asyncNote}>
                            Você enviará o convite agora. @{displayUsername} precisa aceitar para o duelo ao vivo começar.
                        </Text>
                    </View>

                    {/* Remaining counter */}
                    <View style={styles.limitContainer}>
                        <Text style={styles.limitLabel}>Desafios restantes hoje:</Text>
                        <Text style={[styles.limitValue, { color: getLimitColor() }]}>
                            {remainingChallenges}/4
                        </Text>
                    </View>

                    {/* Buttons */}
                    <View style={styles.buttons}>
                        <TouchableOpacity
                            style={styles.cancelButton}
                            onPress={handleCancel}
                            activeOpacity={0.7}
                        >
                            <Text style={styles.cancelText}>Cancelar</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.confirmButtonContainer}
                            onPress={handleConfirm}
                            disabled={isLoading}
                            activeOpacity={0.8}
                        >
                            <LinearGradient
                                colors={['#9333EA', '#7C3AED']}
                                style={styles.confirmButton}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 0 }}
                            >
                                {isLoading ? (
                                    <ActivityIndicator color="#FFFFFF" size="small" />
                                ) : (
                                    <>
                                        <Text style={styles.confirmIcon}>⚔️</Text>
                                        <Text style={styles.confirmText}>Enviar Desafio</Text>
                                    </>
                                )}
                            </LinearGradient>
                        </TouchableOpacity>
                    </View>
                </Animated.View>
            </Animated.View>
        </Modal>
    );
});

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
    },

    modal: {
        width: '100%',
        borderRadius: 24,
        padding: 28,
        alignItems: 'center',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
    },

    icon: {
        fontSize: 56,
        marginBottom: 16,
    },

    title: {
        fontSize: 22,
        fontWeight: '700',
        color: '#FFFFFF',
        marginBottom: 20,
        textAlign: 'center',
    },

    infoBox: {
        width: '100%',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        borderRadius: 16,
        padding: 16,
        marginBottom: 16,
        borderWidth: 1,
        borderColor: 'rgba(139, 92, 246, 0.2)',
    },

    infoRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 10,
    },

    infoIcon: {
        fontSize: 16,
        marginRight: 10,
        width: 24,
    },

    infoText: {
        fontSize: 14,
        color: 'rgba(255, 255, 255, 0.8)',
    },

    divider: {
        height: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        marginVertical: 12,
    },

    asyncNote: {
        fontSize: 13,
        color: 'rgba(255, 255, 255, 0.6)',
        textAlign: 'center',
        lineHeight: 18,
    },

    limitContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 24,
    },

    limitLabel: {
        fontSize: 14,
        color: 'rgba(255, 255, 255, 0.5)',
        marginRight: 8,
    },

    limitValue: {
        fontSize: 16,
        fontWeight: '700',
    },

    buttons: {
        flexDirection: 'row',
        gap: 12,
        width: '100%',
    },

    cancelButton: {
        flex: 0.4,
        paddingVertical: 14,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },

    cancelText: {
        fontSize: 15,
        fontWeight: '600',
        color: 'rgba(255, 255, 255, 0.7)',
    },

    confirmButtonContainer: {
        flex: 0.6,
        borderRadius: 12,
        overflow: 'hidden',
    },

    confirmButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 14,
        gap: 8,
    },

    confirmIcon: {
        fontSize: 16,
    },

    confirmText: {
        fontSize: 15,
        fontWeight: '700',
        color: '#FFFFFF',
    },
});

export default ChallengeConfirmModal;
